import React, { useState } from "react";
import { Briefcase, MapPin, Clock, Send, Bot, ArrowLeft, Building2, User, MessageSquare } from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";

const HEADER_LOGO_URL = "/header_logo.jpeg";
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const jobListings = [
  {
    id: 1,
    title: "Fire Safety Engineer",
    location: "Pune, Maharashtra",
    type: "Full-time",
    experience: "3-5 years",
    description: "Design and implement fire protection systems. Conduct fire risk assessments and ensure NBC/NFPA compliance.",
    requirements: ["B.Tech in Fire Safety Engineering", "NBC/NFPA certification", "Experience with sprinkler and detection systems"]
  },
  {
    id: 2,
    title: "Security System Specialist",
    location: "Pune, Maharashtra",
    type: "Full-time",
    experience: "2-4 years",
    description: "Install and maintain AI-enhanced CCTV, access control and intrusion detection systems.",
    requirements: ["Diploma/B.Tech in Electronics", "Experience with CCTV and access control", "Knowledge of networking"]
  },
  {
    id: 3,
    title: "AI/ML Developer",
    location: "Pune, Maharashtra / Remote",
    type: "Full-time",
    experience: "2-5 years",
    description: "Develop AI-powered tools for fire safety analysis, project management and predictive analytics.",
    requirements: ["B.Tech/M.Tech in Computer Science", "Python, TensorFlow/PyTorch", "Experience with LLMs and computer vision"]
  },
  {
    id: 4,
    title: "Project Manager - Fire Safety",
    location: "Pune, Maharashtra",
    type: "Full-time",
    experience: "5-8 years",
    description: "Lead fire safety projects from design to commissioning. Coordinate with clients and ensure timely delivery.",
    requirements: ["B.Tech + MBA preferred", "PMP certification", "Experience in fire safety projects"]
  },
  {
    id: 5,
    title: "Sales Executive - Safety Solutions",
    location: "Pune, Maharashtra",
    type: "Full-time",
    experience: "1-3 years",
    description: "Drive sales of fire safety and security solutions. Build relationships with clients in construction and infrastructure sectors.",
    requirements: ["Any Graduate", "Strong communication skills", "Experience in B2B sales"]
  },
  {
    id: 6,
    title: "Disaster Management Consultant",
    location: "Pune, Maharashtra",
    type: "Full-time",
    experience: "4-6 years",
    description: "Provide advisory services on disaster preparedness, emergency response planning and safety audits.",
    requirements: ["B.Tech/M.Tech in Safety Engineering", "Certifications in disaster management", "Experience with risk assessment"]
  }
];

export default function CareerPage() {
  const [messages, setMessages] = useState([
    { role: "assistant", content: "Hello! I'm the Kavach Career AI Assistant. I can help you with:\n\n• Available job positions\n• Company culture and benefits\n• Application process\n• General career FAQs\n\nHow can I assist you today?" }
  ]);
  const [inputMessage, setInputMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const handleSendMessage = async () => {
    if (!inputMessage.trim()) return;

    const userMessage = { role: "user", content: inputMessage };
    setMessages(prev => [...prev, userMessage]);
    setInputMessage("");
    setIsLoading(true);

    try {
      const response = await fetch(`${BACKEND_URL}/api/career-chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          message: inputMessage,
          conversation_history: messages
        })
      });

      const data = await response.json();
      setMessages(prev => [...prev, { role: "assistant", content: data.response }]);
    } catch (error) {
      setMessages(prev => [...prev, { 
        role: "assistant", 
        content: "I apologize, but I'm having trouble connecting right now. Please email us at info@kavachinfra.in or call +91 9890838368." 
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="fixed top-0 w-full bg-white border-b border-slate-200 z-50 shadow-sm">
        <div className="max-w-7xl mx-auto px-6 py-3 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <img src={HEADER_LOGO_URL} alt="Kavach Logo" className="h-14 w-14 object-contain rounded-full" />
            <div>
              <h1 className="text-xl font-bold" style={{color: '#003366'}}>KAVACH INFRA SOLUTIONS LLP</h1>
              <p className="text-xs" style={{color: '#D4AF37', fontWeight: 600}}>रक्षा | सुरक्षा | सजगता</p>
            </div>
          </div>
          <Button
            onClick={() => window.location.href = '/'}
            variant="outline"
            style={{borderColor: '#003366', color: '#003366'}}
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Home
          </Button>
        </div>
      </header>

      {/* Hero Section */}
      <section className="pt-28 pb-16 px-6 bg-gradient-to-br from-blue-50 via-slate-50 to-amber-50">
        <div className="max-w-7xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-amber-50 border-2 border-amber-300 rounded-full mb-6 shadow-sm">
            <Briefcase className="w-4 h-4" style={{color: '#D4AF37'}} />
            <span className="text-sm font-bold" style={{color: '#003366'}}>Join Our Team</span>
          </div>
          <h1 className="text-4xl lg:text-5xl font-bold mb-6" style={{color: '#003366'}}>
            Build Your Career with Kavach
          </h1>
          <p className="text-lg text-slate-600 max-w-3xl mx-auto mb-8 leading-relaxed">
            Be part of a team that's revolutionizing safety and security with AI. We're looking for passionate individuals who want to make a difference.
          </p>
          <div className="flex flex-wrap gap-8 justify-center">
            <div className="text-center">
              <p className="text-3xl font-bold" style={{color: '#D4AF37'}}>50+</p>
              <p className="text-sm text-slate-600">Team Members</p>
            </div>
            <div className="text-center">
              <p className="text-3xl font-bold" style={{color: '#D4AF37'}}>AI-Driven</p>
              <p className="text-sm text-slate-600">Innovation</p>
            </div>
            <div className="text-center">
              <p className="text-3xl font-bold" style={{color: '#D4AF37'}}>Growth</p>
              <p className="text-sm text-slate-600">Opportunities</p>
            </div>
          </div>
        </div>
      </section>

      {/* Job Listings */}
      <section className="py-20 px-6 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-4">
              Current Openings
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Explore exciting career opportunities across various domains
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-6">
            {jobListings.map(job => (
              <Card key={job.id} className="border-2 hover:shadow-xl transition-all" style={{borderColor: '#e2e8f0'}}>
                <CardContent className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-start gap-3">
                      <div className="w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0" style={{backgroundColor: '#003366'}}>
                        <Briefcase className="w-6 h-6" style={{color: '#D4AF37'}} />
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-slate-900 mb-1">{job.title}</h3>
                        <div className="flex flex-wrap gap-3 text-sm text-slate-600">
                          <div className="flex items-center gap-1">
                            <MapPin className="w-4 h-4" />
                            <span>{job.location}</span>
                          </div>
                          <div className="flex items-center gap-1">
                            <Clock className="w-4 h-4" />
                            <span>{job.type}</span>
                          </div>
                          <div className="flex items-center gap-1">
                            <User className="w-4 h-4" />
                            <span>{job.experience}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <p className="text-slate-600 mb-4 leading-relaxed">
                    {job.description}
                  </p>
                  
                  <div className="mb-4">
                    <p className="text-sm font-semibold text-slate-700 mb-2">Requirements:</p>
                    <ul className="space-y-1">
                      {job.requirements.map((req, idx) => (
                        <li key={idx} className="text-sm text-slate-600 flex items-start gap-2">
                          <span className="text-amber-500 mt-0.5">•</span>
                          <span>{req}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  
                  <Button
                    onClick={() => window.location.href = `mailto:info@kavachinfra.in?subject=Application for ${job.title}`}
                    style={{backgroundColor: '#003366'}}
                    className="hover:opacity-90 text-white w-full"
                  >
                    Apply Now
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* AI Career Chatbot */}
      <section className="py-20 px-6 bg-slate-50">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <div className="flex items-center justify-center gap-2 mb-4">
              <Bot className="w-8 h-8" style={{color: '#003366'}} />
              <h2 className="text-3xl lg:text-4xl font-bold text-slate-900">
                Career AI Assistant
              </h2>
            </div>
            <p className="text-lg text-slate-600">
              Ask me anything about careers at Kavach Infra Solutions LLP
            </p>
          </div>

          <Card className="border-2" style={{borderColor: '#D4AF37'}}>
            <CardContent className="p-0">
              {/* Chat Messages */}
              <div className="h-96 overflow-y-auto p-6 space-y-4">
                {messages.map((msg, idx) => (
                  <div key={idx} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                    <div className={`max-w-[80%] rounded-lg p-4 ${
                      msg.role === 'user' 
                        ? 'bg-blue-900 text-white' 
                        : 'bg-slate-100 text-slate-800'
                    }`}>
                      {msg.role === 'assistant' && (
                        <div className="flex items-center gap-2 mb-2">
                          <Bot className="w-4 h-4" style={{color: '#D4AF37'}} />
                          <span className="text-xs font-semibold" style={{color: '#D4AF37'}}>Kavach AI</span>
                        </div>
                      )}
                      <p className="text-sm leading-relaxed whitespace-pre-line">{msg.content}</p>
                    </div>
                  </div>
                ))}
                {isLoading && (
                  <div className="flex justify-start">
                    <div className="bg-slate-100 rounded-lg p-4">
                      <div className="flex items-center gap-2">
                        <div className="w-2 h-2 bg-slate-400 rounded-full animate-bounce"></div>
                        <div className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{animationDelay: '0.2s'}}></div>
                        <div className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{animationDelay: '0.4s'}}></div>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Chat Input */}
              <div className="border-t border-slate-200 p-4">
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={inputMessage}
                    onChange={(e) => setInputMessage(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
                    placeholder="Ask about job roles, company culture, benefits..."
                    className="flex-1 px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-900"
                    disabled={isLoading}
                  />
                  <Button
                    onClick={handleSendMessage}
                    disabled={isLoading || !inputMessage.trim()}
                    style={{backgroundColor: '#003366'}}
                    className="hover:opacity-90 text-white"
                  >
                    <Send className="w-4 h-4" />
                  </Button>
                </div>
                <p className="text-xs text-slate-500 mt-2">
                  For direct applications, email us at <a href="mailto:info@kavachinfra.in" className="text-blue-800 underline">info@kavachinfra.in</a>
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Why Join Us */}
      <section className="py-20 px-6 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-4">
              Why Join Kavach?
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Benefits and perks that make Kavach a great place to work
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <Card className="border-2 hover:shadow-xl transition-all">
              <CardContent className="p-8 text-center">
                <div className="w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center" style={{backgroundColor: '#003366'}}>
                  <Building2 className="w-8 h-8" style={{color: '#D4AF37'}} />
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-3">Innovative Environment</h3>
                <p className="text-slate-600">
                  Work on cutting-edge AI projects that are transforming the safety and security industry
                </p>
              </CardContent>
            </Card>

            <Card className="border-2 hover:shadow-xl transition-all">
              <CardContent className="p-8 text-center">
                <div className="w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center" style={{backgroundColor: '#003366'}}>
                  <User className="w-8 h-8" style={{color: '#D4AF37'}} />
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-3">Growth & Learning</h3>
                <p className="text-slate-600">
                  Continuous learning opportunities, certifications and career advancement paths
                </p>
              </CardContent>
            </Card>

            <Card className="border-2 hover:shadow-xl transition-all">
              <CardContent className="p-8 text-center">
                <div className="w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center" style={{backgroundColor: '#003366'}}>
                  <MessageSquare className="w-8 h-8" style={{color: '#D4AF37'}} />
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-3">Competitive Benefits</h3>
                <p className="text-slate-600">
                  Attractive compensation, health insurance, performance bonuses and flexible work options
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>
    </div>
  );
}
